export const getAddressFromMapboxFeature = (feature: any) => {
  if (!feature) return {};

  // MapBox returns data directly on the feature object
  const context = feature.context || {};

  // Get street address - MapBox returns it directly as 'address'
  const address = feature.address || "";

  // Extract components from context
  const city = context.place?.name || "";
  const state = context.region?.region_code || context.region?.name || "";
  const zipcode = context.postcode?.name || "";
  const country = context.country?.country_code?.toUpperCase() || "US";

  return {
    address: address.trim(),
    city: city.trim(),
    state: state.trim(),
    zipcode: zipcode.trim(),
    country: country.trim(),
  };
};

// Cache for user location to avoid repeated permission prompts
let cachedLocation: { longitude?: number; latitude?: number } | null = null;
let locationPromiseInProgress: Promise<{
  longitude?: number;
  latitude?: number;
}> | null = null;
let lastLocationAttempt = 0;
const LOCATION_RETRY_INTERVAL = 10000; // Retry every 10 seconds if no cached location

export const getUserLocation = async (): Promise<{
  longitude?: number;
  latitude?: number;
}> => {
  // Return cached location if available
  if (cachedLocation) {
    return cachedLocation;
  }

  // If a location request is already in progress, return that promise
  if (locationPromiseInProgress) {
    return locationPromiseInProgress;
  }

  // If we recently tried and failed, don't retry immediately
  const now = Date.now();
  if (
    !cachedLocation &&
    lastLocationAttempt &&
    now - lastLocationAttempt < LOCATION_RETRY_INTERVAL
  ) {
    return {};
  }

  try {
    // Only use GPS location, no IP-based fallback
    if ("geolocation" in navigator) {
      lastLocationAttempt = Date.now();
      locationPromiseInProgress = new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          // Don't wait for user response, proceed without location
          resolve({});
          locationPromiseInProgress = null;
        }, 500); // Very short timeout - if user hasn't already granted permission, don't wait

        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(timeoutId);
            const location = {
              longitude: position.coords.longitude,
              latitude: position.coords.latitude,
            };
            cachedLocation = location;
            lastLocationAttempt = 0; // Reset on success so future calls use cache
            resolve(location);
            locationPromiseInProgress = null;
          },
          (error) => {
            clearTimeout(timeoutId);
            // If GPS fails, return empty object (no proximity bias)
            resolve({});
            locationPromiseInProgress = null;

            // If user denied permission, don't retry for a while
            if (error.code === error.PERMISSION_DENIED) {
              lastLocationAttempt = Date.now();
            }
          },
          {
            timeout: 10000, // Geolocation API timeout (separate from our timeout)
            maximumAge: 300000, // Cache browser's position for 5 minutes
            enableHighAccuracy: false, // Faster but less accurate - good for proximity hints
          }
        );
      });

      return locationPromiseInProgress;
    }
    // No geolocation API available
    return {};
  } catch {
    locationPromiseInProgress = null;
    return {};
  }
};
